import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface InsurancePolicyListItem {
  id: string;
  legalEntityId?: string;
  policyNumber: string;
  producerId?: string | null;
  productName: string;
  lineOfBusiness: string;
  status: 'Draft' | 'Bound' | 'Issued' | 'InForce' | 'Suspended' | 'Cancelled' | 'Expired' | 'Renewed' | string;
  premiumAmount: number;
  currencyCode: string;
  effectiveFromUtc: string;
  effectiveToUtc: string;
  issueDateUtc?: string | null;
  suspendedAtUtc?: string | null;
  cancelledAtUtc?: string | null;
  expiredAtUtc?: string | null;
  lastLifecycleReason?: string | null;
}

export interface InsurancePolicyTerm {
  id: string;
  policyId: string;
  termNumber: number;
  fromUtc: string;
  toUtc: string;
  premiumAmount: number;
  currencyCode: string;
}

export interface InsurancePolicyCoverage {
  id: string;
  policyId: string;
  coverageCode: string;
  coverageName: string;
  sumInsured: number;
  premiumAmount: number;
  deductibleAmount: number | null;
  currencyCode: string;
}

export interface InsurancePolicyPartyRole {
  id: string;
  policyId: string;
  partyId: string;
  role: string;
}

export interface InsuranceEndorsement {
  id: string;
  policyId: string;
  endorsementNumber: string;
  type: 'ChangeCoverage' | 'ChangeRisk' | 'ChangeParty' | 'Cancellation' | 'Other' | string;
  effectiveAtUtc: string;
  status: 'Draft' | 'Applied' | 'Voided' | string;
  deltaJson: string;
  premiumAdjustment: number;
  appliedAtUtc: string | null;
  voidedAtUtc: string | null;
}

export interface InsuranceRenewalOffer {
  id: string;
  policyId: string;
  offerNumber: string;
  offerValidUntilUtc: string;
  newProductVersionId: string | null;
  premiumBreakdownId: string | null;
  premiumAmount: number;
  currencyCode: string;
  status: 'Draft' | 'Active' | 'Accepted' | 'Expired' | string;
  acceptedAtUtc: string | null;
  renewedPolicyId: string | null;
}

export interface InsuranceAuditLogItem {
  id: string;
  entityName: string;
  entityId: string;
  action: string;
  actorUserId: string | null;
  payloadJson: string;
  createdAtUtc: string;
}

export interface InsurancePolicyDocument {
  documentCode: string;
  title: string;
  documentType: string;
  createdAtUtc: string;
  sourceRef: string;
}

export interface InsurancePolicyDocumentTemplate {
  id: string;
  legalEntityId: string;
  name: string;
  description: string;
  templateHtml: string;
  variablesJson: string;
  isActive: boolean;
  createdAtUtc: string;
  updatedAtUtc?: string | null;
}

export interface UpsertInsurancePolicyDocumentTemplatePayload {
  legalEntityId: string;
  name: string;
  description: string;
  templateHtml: string;
  variablesJson: string;
  isActive: boolean;
}

export interface InsurancePolicyDraftRenderPayload {
  templateId?: string | null;
  templateHtml?: string | null;
  variables?: Record<string, string> | null;
}

export interface InsurancePolicyDraftRenderResult {
  policyId: string;
  templateId?: string | null;
  templateName?: string | null;
  usedDefaultPreview: boolean;
  renderedHtml: string;
  variables: Record<string, string>;
}

export interface InsuranceProductVersionListItem {
  id: string;
  versionNumber: number;
  status: 'Draft' | 'Published' | 'Retired';
  publishedAtUtc: string | null;
  minPremium: number | null;
  maxSumInsured: number | null;
}

export interface InsuranceProductVersionCatalogListItem extends InsuranceProductVersionListItem {
  productId: string;
  productCode: string;
  productName: string;
  lineOfBusiness: string;
}

export interface InsuranceProductListItem {
  id: string;
  legalEntityId: string;
  code: string;
  name: string;
  lineOfBusiness: string;
  offeringId: string | null;
  policyDocumentTemplateId?: string | null;
  isActive: boolean;
  effectiveFromUtc: string;
  effectiveToUtc: string | null;
}

export interface InsuranceProductDetail extends InsuranceProductListItem {
  versions: InsuranceProductVersionListItem[];
}

export interface InsuranceProductVersionDetail {
  id: string;
  productId: string;
  legalEntityId: string;
  versionNumber: number;
  status: 'Draft' | 'Published' | 'Retired';
  publishedAtUtc: string | null;
  termsAndConditionsJson: string;
  minPremium: number | null;
  maxSumInsured: number | null;
  riskSchemaId: string | null;
  uwFormDefinitionId: string | null;
}

export interface InsuranceCoverageItem {
  id: string;
  productVersionId: string;
  code: string;
  name: string;
  coverageType: 'Mandatory' | 'Optional' | 'Rider' | string;
  defaultSumInsured: number | null;
  minSumInsured: number | null;
  maxSumInsured: number | null;
  isDefaultSelected: boolean;
}

export interface InsuranceClauseItem {
  id: string;
  productVersionId: string;
  clauseCode: string;
  title: string;
  bodyText: string;
  clauseType: 'Inclusion' | 'Exclusion' | 'Condition' | string;
  appliesToCoverageId: string | null;
}

export interface InsuranceQuestionItem {
  id: string;
  productVersionId: string;
  questionKey: string;
  dataType: string;
  required: boolean;
  validationJson: string;
  mapsToRatingFactor: string | null;
}

export interface InsuranceVersionValidationResult {
  isValid: boolean;
  errors: string[];
}

export interface InsuranceVersionExportBundle {
  product: InsuranceProductDetail;
  version: InsuranceProductVersionDetail;
  coverages: InsuranceCoverageItem[];
  clauses: InsuranceClauseItem[];
  questions: InsuranceQuestionItem[];
}

export interface InsuranceRatingPlan {
  id: string;
  code: string;
  name: string;
  productVersionId: string;
  currencyCode: string;
  calculationMode: 'Table' | 'Formula' | 'Hybrid' | 'ExternalAdapter' | string;
  externalAdapterKey: string | null;
  rateTableJson: string;
}

export interface UpsertInsuranceRatingPlanPayload {
  code: string;
  name: string;
  productVersionId: string;
  legalEntityId: string;
  currencyCode: string;
  calculationMode: string;
  externalAdapterKey: string | null;
  rateTableJson: string;
}

export interface InsuranceRatingCoverageSelection {
  coverageCode: string;
  sumInsured: number;
  selected: boolean;
}

export interface InsuranceRatingCalculatePayload {
  productVersionId: string;
  ratingPlanId: string | null;
  currencyCode: string;
  factors: Record<string, number>;
  coverages: InsuranceRatingCoverageSelection[];
}

export interface InsurancePremiumBreakdown {
  quoteId: string | null;
  productVersionId: string;
  ratingPlanId: string | null;
  basePremium: number;
  totalLoadings: number;
  totalDiscounts: number;
  totalTaxesAndFees: number;
  totalPremium: number;
  currencyCode: string;
  loadings: InsurancePremiumLineItem[];
  discounts: InsurancePremiumLineItem[];
  taxes: InsurancePremiumLineItem[];
  fees: InsurancePremiumLineItem[];
  breakdownJson: string;
  pricingHash: string;
  calculatedAtUtc: string;
}

export interface InsurancePremiumLineItem {
  code: string;
  amount: number;
  sourceRuleId: string;
}

export interface InsuranceRatingRepricePayload {
  quoteId: string;
  calculation: InsuranceRatingCalculatePayload;
}

export interface InsuranceRatingPlanTables {
  ratingPlanId: string;
  rateTableJson: string;
}

export interface InsuranceRatingSimulationScenario {
  scenarioKey: string;
  factors: Record<string, number>;
  coverages: InsuranceRatingCoverageSelection[];
}

export interface InsuranceRatingPlanSimulationPayload {
  productVersionId: string;
  currencyCode: string;
  scenarios: InsuranceRatingSimulationScenario[];
}

export interface InsuranceRatingSimulationResult {
  scenarioKey: string;
  totalPremium: number;
  pricingHash: string;
}

export interface InsuranceRatingPlanSimulationResponse {
  ratingPlanId: string;
  results: InsuranceRatingSimulationResult[];
}

export interface InsuranceQuoteListItem {
  id: string;
  quoteNumber: string;
  status: 'Draft' | 'Priced' | 'Expired' | 'ConvertedToProposal' | 'Cancelled' | string;
  productVersionId: string;
  customerAccountId: string;
  validUntilUtc: string;
  pricingSnapshotHash: string | null;
}

export interface InsuranceQuoteOption {
  id: string;
  optionLabel: string;
  coverageSelectionJson: string;
  premiumBreakdownId: string | null;
  pricingSnapshotHash: string;
}

export interface InsuranceQuoteDetail extends InsuranceQuoteListItem {
  channelId: string | null;
  producerId: string | null;
  territoryId: string | null;
  riskObjectsJson: string;
  selectedCoveragesJson: string;
  premiumBreakdownId: string | null;
  pricingSnapshotJson: string;
  pricedAtUtc: string | null;
  options: InsuranceQuoteOption[];
}

export interface InsuranceProposalListItem {
  id: string;
  proposalNumber: string;
  status: 'Draft' | 'Submitted' | 'Accepted' | 'Declined' | 'Expired' | 'Bound' | string;
  quoteNumber: string;
  quoteId: string;
  productVersionId: string;
  customerAccountId: string;
  boundPolicyId: string | null;
}

export interface InsuranceProposalDetail extends InsuranceProposalListItem {
  policyholderPartyId: string;
  insuredPartiesJson: string;
  beneficiariesJson: string;
  paymentPlanPreference: string | null;
  pricingSnapshotHash: string;
  submittedAtUtc: string | null;
  acceptedAtUtc: string | null;
  declinedAtUtc: string | null;
  expiredAtUtc: string | null;
  boundAtUtc: string | null;
}

export interface InsuranceProducerListItem {
  id: string;
  code: string;
  name: string;
  producerType: 'Agent' | 'Broker' | 'Employee' | 'Bancassurance' | string;
  partyId: string;
  licenseNumber: string;
  licenseExpiryUtc: string;
  status: 'Active' | 'Suspended' | 'Terminated' | string;
}

export interface InsuranceProducerAgreement {
  id: string;
  producerId: string;
  agencyId: string;
  productVersionId: string | null;
  commissionPlanId: string;
  effectiveFromUtc: string;
  effectiveToUtc: string | null;
  territoryId: string | null;
  isExclusive: boolean;
}

export interface InsuranceProducerStatusTransition {
  id: string;
  producerId: string;
  fromStatus: string;
  toStatus: string;
  reason: string | null;
  changedByUserId: string | null;
  changedAtUtc: string;
}

export interface InsuranceProducerDetail extends InsuranceProducerListItem {
  legalEntityId: string;
  agreements: InsuranceProducerAgreement[];
  statusTransitions: InsuranceProducerStatusTransition[];
}

export interface CreateInsuranceProducerPayload {
  legalEntityId: string;
  code: string;
  name: string;
  producerType: string;
  partyId: string;
  licenseNumber: string;
  licenseExpiryUtc: string;
}

export interface UpdateInsuranceProducerPayload {
  name: string;
  producerType: string;
  partyId: string;
  licenseNumber: string;
  licenseExpiryUtc: string;
}

export interface InsuranceProducerLicenseValidation {
  producerId: string;
  isValid: boolean;
  isExpired: boolean;
  status: string;
  licenseExpiryUtc: string;
  message: string | null;
}

export interface InsuranceAgencyListItem {
  id: string;
  code: string;
  name: string;
  parentAgencyId: string | null;
  organizationUnitId: string | null;
}

export interface InsuranceAgencyDetail extends InsuranceAgencyListItem {
  legalEntityId: string;
}

export interface CreateInsuranceAgencyPayload {
  legalEntityId: string;
  code: string;
  name: string;
  parentAgencyId: string | null;
  organizationUnitId: string | null;
}

export interface UpdateInsuranceAgencyPayload {
  name: string;
  parentAgencyId: string | null;
  organizationUnitId: string | null;
}

export interface InsuranceTerritory {
  id: string;
  legalEntityId: string;
  code: string;
  name: string;
  regionJson: string;
}

export interface CreateInsuranceTerritoryPayload {
  legalEntityId: string;
  code: string;
  name: string;
  regionJson: string;
}

export interface UpdateInsuranceTerritoryPayload {
  name: string;
  regionJson: string;
}

export interface UpsertInsuranceProducerAgreementsPayload {
  items: Array<{
    agencyId: string;
    productVersionId: string | null;
    commissionPlanId: string;
    effectiveFromUtc: string;
    effectiveToUtc: string | null;
    territoryId: string | null;
    isExclusive: boolean;
  }>;
}

export interface InsuranceChannelBinding {
  id: string;
  salesChannelId: string;
  defaultProducerId: string | null;
  allowedProductIdsJson: string;
  quoteValidityHours: number;
  requiresUwApproval: boolean;
  territoryId: string | null;
  territoryEnforcementMode: 'None' | 'Warn' | 'Block' | string;
  customerConflictMode: 'Allow' | 'Warn' | 'Block' | string;
}

export interface UpsertInsuranceChannelBindingPayload {
  legalEntityId: string;
  defaultProducerId: string | null;
  allowedProductIdsJson: string;
  quoteValidityHours: number;
  requiresUwApproval: boolean;
  territoryId: string | null;
  territoryEnforcementMode: string;
  customerConflictMode: string;
}

export interface CreateInsuranceQuotePayload {
  legalEntityId: string;
  productVersionId: string;
  customerAccountId: string;
  channelId: string | null;
  producerId: string | null;
  validUntilUtc: string;
  riskObjectsJson: string;
  selectedCoveragesJson: string;
}

export interface PriceInsuranceQuotePayload {
  ratingPlanId: string | null;
  currencyCode: string;
  factors: Record<string, number>;
  coverages: InsuranceRatingCoverageSelection[];
}

export interface CreateInsuranceQuoteOptionPayload {
  optionLabel: string;
  coverages: InsuranceRatingCoverageSelection[];
  ratingPlanId: string | null;
  currencyCode: string;
  factors: Record<string, number>;
}

export interface InsuranceUwRule {
  id: string;
  productVersionId: string;
  priority: number;
  conditionJson: string;
  action: 'AutoApprove' | 'AutoDecline' | 'Refer' | 'RequireInfo' | string;
  maxSumInsured: number | null;
  zoneCodesJson: string;
}

export interface PutInsuranceUwRuleItemPayload {
  priority: number;
  conditionJson: string;
  action: string;
  maxSumInsured: number | null;
  zoneCodesJson: string;
}

export interface InsuranceUwCaseListItem {
  id: string;
  proposalId: string;
  proposalNumber: string;
  status: 'Open' | 'Approved' | 'Declined' | 'Referred' | 'PendingInfo' | string;
  riskScore: number | null;
  decisionReason: string | null;
  workflowInstanceId: string | null;
  matchedUwRuleId: string | null;
  totalSumInsured: number | null;
  ruleEvaluationTraceJson: string;
  createdAtUtc: string;
}

export interface InsuranceUwReferral {
  id: string;
  uwCaseId: string;
  assignedToRole: string;
  dueAtUtc: string;
  notes: string;
}

export interface InsuranceUwDecision {
  id: string;
  uwCaseId: string;
  decision: 'Approved' | 'Declined' | string;
  decidedByUserId: string | null;
  overrideReason: string | null;
  aiRecommendationJson: string | null;
  createdAtUtc: string;
}

export interface InsuranceUwCaseDetail extends InsuranceUwCaseListItem {
  decidedAtUtc: string | null;
  referrals: InsuranceUwReferral[];
  decisions: InsuranceUwDecision[];
}

export interface ApproveInsuranceUwCasePayload {
  overrideReason: string | null;
  aiRecommendationJson: string | null;
}

export interface DeclineInsuranceUwCasePayload {
  decisionReason: string;
  overrideReason: string | null;
  aiRecommendationJson: string | null;
}

export interface ReferInsuranceUwCasePayload {
  assignedToRole: string;
  dueAtUtc: string;
  notes: string | null;
  workflowInstanceId: string | null;
}

export interface RequestInfoInsuranceUwCasePayload {
  notes: string;
}

export interface ConvertQuoteToProposalPayload {
  policyholderPartyId: string;
  insuredPartiesJson: string;
  beneficiariesJson: string;
  paymentPlanPreference: string | null;
}

export interface CreateInsuranceProposalPayload {
  legalEntityId: string;
  quoteId: string;
  productVersionId: string;
  customerAccountId: string;
  policyholderPartyId: string;
  insuredPartiesJson: string;
  beneficiariesJson: string;
  paymentPlanPreference: string | null;
  pricingSnapshotJson: string;
  pricingSnapshotHash: string;
}

export interface BindInsuranceProposalPayload {
  paymentIntentId: string | null;
  effectiveFromUtc: string | null;
  effectiveToUtc: string | null;
}

export interface CreateInsuranceProductPayload {
  legalEntityId: string;
  code: string;
  name: string;
  lineOfBusiness: string;
  offeringId: string | null;
  policyDocumentTemplateId: string | null;
  isActive: boolean;
  effectiveFromUtc: string;
  effectiveToUtc: string | null;
}

export interface UpdateInsuranceProductPayload {
  name: string;
  lineOfBusiness: string;
  offeringId: string | null;
  policyDocumentTemplateId: string | null;
  isActive: boolean;
  effectiveFromUtc: string;
  effectiveToUtc: string | null;
}

export interface CreateInsuranceProductVersionPayload {
  legalEntityId: string;
  versionNumber: number;
  termsAndConditionsJson: string;
  minPremium: number | null;
  maxSumInsured: number | null;
  riskSchemaId: string | null;
  uwFormDefinitionId: string | null;
}

export interface UpdateInsuranceProductVersionPayload {
  termsAndConditionsJson: string;
  minPremium: number | null;
  maxSumInsured: number | null;
  riskSchemaId: string | null;
  uwFormDefinitionId: string | null;
}

export interface UpsertInsuranceCoveragesPayload {
  items: Array<{
    code: string;
    name: string;
    coverageType: string;
    defaultSumInsured: number | null;
    minSumInsured: number | null;
    maxSumInsured: number | null;
    isDefaultSelected: boolean;
  }>;
}

export interface UpsertInsuranceClausesPayload {
  items: Array<{
    clauseCode: string;
    title: string;
    bodyText: string;
    clauseType: string;
    appliesToCoverageId: string | null;
  }>;
}

export interface UpsertInsuranceQuestionsPayload {
  items: Array<{
    questionKey: string;
    dataType: string;
    required: boolean;
    validationJson: string;
    mapsToRatingFactor: string | null;
  }>;
}

export interface InsurancePolicyOption {
  id: string;
  policyNumber: string;
  legalEntityId: string;
  currencyCode: string;
}

export interface InsuranceClaimListItem {
  id: string;
  claimNumber: string;
  policyNumber: string;
  producerId: string | null;
  lineOfBusiness: string;
  status: 'Reported' | 'Open' | 'UnderInvestigation' | 'Approved' | 'Declined' | 'PartiallyPaid' | 'Paid' | 'Closed' | 'Reopened' | string;
  reserveAmount: number;
  paidAmount: number;
  currencyCode: string;
  openedAtUtc: string;
  lossDateUtc: string;
  fnolChannel: 'Portal' | 'Agent' | 'CallCenter' | 'Api' | string;
}

export interface CreateInsurancePolicyPayload {
  legalEntityId: string;
  policyNumber: string;
  productName: string;
  lineOfBusiness: string;
  status: string;
  premiumAmount: number;
  currencyCode: string;
  effectiveFromUtc: string;
  effectiveToUtc: string;
}

export interface UpdateInsurancePolicyPayload {
  productName: string;
  lineOfBusiness: string;
  status: string;
  premiumAmount: number;
  currencyCode: string;
  effectiveFromUtc: string;
  effectiveToUtc: string;
}

export interface InsurancePolicyLifecycleActionPayload {
  reason: string | null;
}

export interface InsuranceCommissionPlanListItem {
  id: string;
  code: string;
  name: string;
  basis: 'PercentOfPremium' | 'Fixed' | 'Tiered' | string;
  clawbackMonths: number | null;
  isActive: boolean;
}

export interface InsuranceCommissionPlanDetail extends InsuranceCommissionPlanListItem {
  legalEntityId: string;
  rulesJson: string;
}

export interface CreateInsuranceCommissionPlanPayload {
  legalEntityId: string;
  code: string;
  name: string;
  basis: string;
  rulesJson: string;
  clawbackMonths: number | null;
  isActive: boolean;
}

export interface UpdateInsuranceCommissionPlanPayload {
  name: string;
  basis: string;
  rulesJson: string;
  clawbackMonths: number | null;
  isActive: boolean;
}

export interface InsuranceCommissionAccrual {
  id: string;
  policyId: string;
  producerId: string;
  period: string;
  amount: number;
  status: 'Accrued' | 'Paid' | 'ClawedBack' | string;
  sourceEventType: string;
  idempotencyKey: string;
  settlementId: string | null;
}

export interface InsuranceCommissionSettlementListItem {
  id: string;
  settlementNumber: string;
  producerId: string;
  totalAmount: number;
  currencyCode: string;
  status: 'Draft' | 'Approved' | 'Paid' | string;
  paymentBatchId: string | null;
  approvedAtUtc: string | null;
  paidAtUtc: string | null;
}

export interface InsuranceCommissionSettlementDetail extends InsuranceCommissionSettlementListItem {
  legalEntityId: string;
  accruals: InsuranceCommissionAccrual[];
}

export interface CreateInsuranceCommissionSettlementPayload {
  legalEntityId: string;
  producerId: string;
  period: string | null;
  currencyCode: string;
}

export interface PayInsuranceCommissionSettlementPayload {
  paymentBatchId: string | null;
}

export interface InsurancePremiumScheduleLine {
  id: string;
  scheduleId: string;
  policyId: string;
  dueDateUtc: string;
  amount: number;
  status: 'Open' | 'PartiallyPaid' | 'Paid' | 'WrittenOff' | 'Overdue' | string;
  invoiceId: string | null;
}

export interface InsurancePremiumSchedule {
  id: string;
  policyId: string;
  installmentCount: number;
  frequency: 'Single' | 'Monthly' | 'Quarterly' | 'Custom' | string;
  dunningSuspendAfterDays: number | null;
  lines: InsurancePremiumScheduleLine[];
}

export interface CreateInsurancePremiumSchedulePayload {
  legalEntityId: string;
  installmentCount: number;
  frequency: string;
  firstDueDateUtc: string | null;
  dunningSuspendAfterDays: number | null;
  lines: Array<{
    dueDateUtc: string;
    amount: number;
  }> | null;
}

export interface InsurancePremiumCollection {
  id: string;
  invoiceId: string;
  collectedAmount: number;
  collectedAtUtc: string;
  paymentReference: string;
}

export interface InsurancePremiumInvoiceListItem {
  id: string;
  invoiceNumber: string;
  policyId: string;
  policyNumber: string;
  producerId: string | null;
  lineOfBusiness: string;
  amount: number;
  taxAmount: number;
  collectedAmount: number;
  outstandingAmount: number;
  status: 'Open' | 'PartiallyPaid' | 'Paid' | 'WrittenOff' | 'Cancelled' | string;
  paymentIntentId: string | null;
  scheduleLineId: string | null;
}

export interface InsurancePremiumInvoiceDetail extends InsurancePremiumInvoiceListItem {
  collections: InsurancePremiumCollection[];
}

export interface CollectInsurancePremiumInvoicePayload {
  collectedAmount: number;
  collectedAtUtc: string | null;
  paymentReference: string;
  paymentIntentId: string | null;
}

export interface RunInsurancePremiumEarningPayload {
  period: string;
  policyId: string | null;
}

export interface InsurancePremiumEarningRun {
  id: string;
  period: string;
  policyId: string;
  earnedAmount: number;
  unearnedRelease: number;
}

export interface InsurancePremiumEarningRunBatch {
  period: string;
  processedCount: number;
  createdCount: number;
  suspendedCount: number;
  runs: InsurancePremiumEarningRun[];
}

export interface CreateInsuranceEndorsementPayload {
  type: string;
  effectiveAtUtc: string;
  deltaJson: string;
  premiumAdjustment: number;
}

export interface CreateInsuranceRenewalOfferPayload {
  offerValidUntilUtc: string;
  newProductVersionId: string | null;
  premiumBreakdownId: string | null;
  premiumAmount: number;
  currencyCode: string;
}

export interface RenewInsurancePolicyPayload {
  renewalOfferId: string;
  effectiveFromUtc: string;
  effectiveToUtc: string;
}

export interface UpsertInsurancePolicyCoveragesPayload {
  items: Array<{
    coverageCode: string;
    coverageName: string;
    sumInsured: number;
    premiumAmount: number;
    deductibleAmount: number | null;
    currencyCode: string;
  }>;
}

export interface UpsertInsurancePolicyPartyRolesPayload {
  items: Array<{
    partyId: string;
    role: string;
  }>;
}

export interface CreateInsuranceClaimPayload {
  policyId: string;
  legalEntityId: string;
  claimNumber: string;
  claimType: string;
  description: string;
  fnolChannel: string;
  lossDateUtc: string;
  reportedAtUtc: string;
  reserveAmount: number;
  currencyCode: string;
  parties: Array<{ partyId: string; role: string }>;
  documents: Array<{ documentType: string; title: string; sourceRef: string }>;
}

export interface UpdateInsuranceClaimPayload {
  claimType: string;
  description: string;
  fnolChannel: string;
  lossDateUtc: string;
  reportedAtUtc: string;
  currencyCode: string;
  notes: string | null;
}

export interface InsuranceClaimReserve {
  id: string;
  claimId: string;
  reserveType: string;
  amount: number;
  movementType: string;
  createdAtUtc: string;
}

export interface InsuranceClaimPayment {
  id: string;
  claimId: string;
  payeePartyId: string;
  amount: number;
  status: string;
  approvalWorkflowId: string | null;
  paymentIntentId: string | null;
  createdAtUtc: string;
}

export interface InsuranceClaimRecovery {
  id: string;
  claimId: string;
  amount: number;
  recoveryType: string;
  reference: string | null;
  createdAtUtc: string;
}

export interface InsuranceClaimDocument {
  id: string;
  claimId: string;
  documentType: string;
  title: string;
  sourceRef: string;
  createdAtUtc: string;
}

export interface InsuranceHealthClaimLine {
  id: string;
  claimId: string;
  serviceDate: string;
  providerId: string;
  serviceCode: string;
  billedAmount: number;
  allowedAmount: number;
  copay: number;
  preAuthorizationId: string | null;
}

export interface InsuranceClaimParty {
  id: string;
  claimId: string;
  partyId: string;
  role: string;
}

export interface InsuranceClaimDetail extends InsuranceClaimListItem {
  policyId: string;
  legalEntityId: string;
  producerId: string | null;
  claimType: string;
  description: string;
  reportedAtUtc: string;
  approvedAtUtc: string | null;
  declinedAtUtc: string | null;
  closedAtUtc: string | null;
  recoveryAmount: number;
  netIncurred: number;
  parties: InsuranceClaimParty[];
  reserves: InsuranceClaimReserve[];
  payments: InsuranceClaimPayment[];
  recoveries: InsuranceClaimRecovery[];
  documents: InsuranceClaimDocument[];
  healthLines: InsuranceHealthClaimLine[];
}

export interface CreateInsuranceClaimReservePayload {
  reserveType: string;
  amount: number;
  movementType: string;
}

export interface ApproveInsuranceClaimPayload {
  reason: string | null;
  approvalWorkflowId: string | null;
}

export interface DeclineInsuranceClaimPayload {
  reason: string;
}

export interface CreateInsuranceClaimPaymentPayload {
  payeePartyId: string;
  amount: number;
  approvalWorkflowId: string | null;
  paymentIntentId: string | null;
}

export interface CloseInsuranceClaimPayload {
  reason: string | null;
}

export interface CreateInsuranceClaimRecoveryPayload {
  amount: number;
  recoveryType: string;
  reference: string | null;
}

export interface InsuranceProviderListItem {
  id: string;
  code: string;
  name: string;
  providerType: 'Hospital' | 'Clinic' | 'Lab' | 'Pharmacy' | 'Physician' | string;
  partyId: string;
  networkTier: 'Preferred' | 'Standard' | 'OutOfNetwork' | string;
  contractStatus: 'Active' | 'Suspended' | 'Expired' | string;
}

export interface InsuranceProviderContract {
  id: string;
  providerId: string;
  effectiveFrom: string;
  effectiveTo: string;
  tariffScheduleId: string;
  coveredServicesJson: string;
}

export interface InsuranceProviderDetail extends InsuranceProviderListItem {
  legalEntityId: string;
  contracts: InsuranceProviderContract[];
}

export interface CreateInsuranceProviderPayload {
  legalEntityId: string;
  code: string;
  name: string;
  providerType: string;
  partyId: string;
  networkTier: string;
  contractStatus: string;
}

export interface UpdateInsuranceProviderPayload {
  name: string;
  providerType: string;
  partyId: string;
  networkTier: string;
  contractStatus: string;
}

export interface UpsertInsuranceProviderContractsPayload {
  items: Array<{
    effectiveFrom: string;
    effectiveTo: string;
    tariffScheduleId: string;
    coveredServicesJson: string;
  }>;
}

export interface InsuranceMemberEligibility {
  id: string;
  policyId: string;
  certificateId: string | null;
  memberPartyId: string;
  eligibleFrom: string;
  eligibleTo: string;
  planCode: string;
  waitingPeriodEndsUtc: string | null;
}

export interface UpsertInsuranceMemberEligibilityPayload {
  items: Array<{
    memberPartyId: string;
    certificateId: string | null;
    eligibleFrom: string;
    eligibleTo: string;
    planCode: string;
    waitingPeriodEndsUtc: string | null;
  }>;
}

export interface CheckInsuranceEligibilityPayload {
  policyId: string;
  memberPartyId: string;
  serviceDateUtc: string;
  providerId: string | null;
  serviceCode: string | null;
}

export interface InsuranceEligibilityCheckResult {
  isEligible: boolean;
  reasonCode: string | null;
  eligibilityId: string | null;
  planCode: string | null;
  waitingPeriodEndsUtc: string | null;
}

export interface CreateInsurancePreAuthorizationPayload {
  policyId: string;
  providerId: string;
  serviceCode: string;
  requestedAmount: number;
  requestedAtUtc: string | null;
  validUntilUtc: string | null;
  status: string;
  approvedAmount: number | null;
}

export interface InsurancePreAuthorization {
  id: string;
  policyId: string;
  providerId: string;
  serviceCode: string;
  status: string;
  requestedAmount: number;
  approvedAmount: number | null;
  requestedAtUtc: string;
  decidedAtUtc: string | null;
  validUntilUtc: string | null;
}

export interface CreateInsuranceHealthClaimLinePayload {
  serviceDate: string;
  providerId: string;
  serviceCode: string;
  billedAmount: number;
  copay: number;
  preAuthorizationId: string | null;
}

export interface InsuranceMasterPolicyListItem {
  id: string;
  masterPolicyNumber: string;
  employerPartyId: string;
  productVersionId: string;
  billingMode: 'MasterBilled' | 'MemberBilled' | string;
  status: 'Draft' | 'Active' | 'Suspended' | 'Cancelled' | 'Expired' | string;
  effectiveFrom: string;
  effectiveTo: string;
  activeMemberCount: number;
}

export interface InsuranceMemberCertificate {
  id: string;
  masterPolicyId: string;
  memberPartyId: string;
  certificateNumber: string;
  status: 'Active' | 'Terminated' | 'Suspended' | string;
  coverageEffectiveFrom: string;
  coverageEffectiveTo: string;
  childPolicyId: string | null;
  planCode: string | null;
}

export interface InsuranceBulkChangeBatch {
  id: string;
  masterPolicyId: string;
  changeType: 'Enroll' | 'Terminate' | 'Update' | string;
  fileId: string | null;
  rowsJson: string;
  status: 'PendingApproval' | 'Approved' | 'Applying' | 'Applied' | string;
  processedCount: number;
  createdAt: string;
  approvedAtUtc: string | null;
  appliedAtUtc: string | null;
}

export interface InsuranceMasterPolicyDetail extends InsuranceMasterPolicyListItem {
  legalEntityId: string;
  members: InsuranceMemberCertificate[];
  bulkChanges: InsuranceBulkChangeBatch[];
  latestTerminationProRataAdjustment: number | null;
}

export interface CreateInsuranceMasterPolicyPayload {
  legalEntityId: string;
  masterPolicyNumber: string;
  employerPartyId: string;
  productVersionId: string;
  billingMode: string;
  effectiveFrom: string;
  effectiveTo: string;
}

export interface UpdateInsuranceMasterPolicyPayload {
  employerPartyId: string;
  productVersionId: string;
  billingMode: string;
  effectiveFrom: string;
  effectiveTo: string;
}

export interface AddInsuranceMemberCertificatePayload {
  memberPartyId: string;
  certificateNumber: string;
  coverageEffectiveFrom: string;
  coverageEffectiveTo: string;
  childPolicyId: string | null;
  planCode: string | null;
}

export interface CreateInsuranceBulkChangeBatchPayload {
  changeType: string;
  fileId: string | null;
  rowsJson: string;
}

export interface InsuranceCensusReportMemberItem {
  certificateId: string;
  memberPartyId: string;
  certificateNumber: string;
  status: string;
  coverageEffectiveFrom: string;
  coverageEffectiveTo: string;
  childPolicyId: string | null;
}

export interface InsuranceCensusReport {
  masterPolicyId: string;
  masterPolicyNumber: string;
  activeCount: number;
  terminatedCount: number;
  totalCount: number;
  members: InsuranceCensusReportMemberItem[];
}

export interface InsuranceReinsuranceProgram {
  id: string;
  treatyId: string;
  productVersionId: string | null;
  lineOfBusiness: string | null;
}

export interface InsuranceReinsuranceTreatyListItem {
  id: string;
  code: string;
  name: string;
  treatyType: 'QuotaShare' | 'Surplus' | 'XOL' | string;
  reinsurerPartyId: string;
  effectiveFrom: string;
  effectiveTo: string;
  cessionPercent: number | null;
  retentionLimit: number | null;
  status: 'Active' | 'Inactive' | string;
}

export interface InsuranceReinsuranceTreatyDetail extends InsuranceReinsuranceTreatyListItem {
  legalEntityId: string;
  layersJson: string;
  programs: InsuranceReinsuranceProgram[];
}

export interface CreateInsuranceReinsuranceTreatyPayload {
  legalEntityId: string;
  code: string;
  name: string;
  treatyType: string;
  reinsurerPartyId: string;
  effectiveFrom: string;
  effectiveTo: string;
  cessionPercent: number | null;
  retentionLimit: number | null;
  layersJson: string | null;
}

export interface UpdateInsuranceReinsuranceTreatyPayload {
  name: string;
  treatyType: string;
  reinsurerPartyId: string;
  effectiveFrom: string;
  effectiveTo: string;
  cessionPercent: number | null;
  retentionLimit: number | null;
  layersJson: string | null;
  status: string;
}

export interface PutInsuranceReinsuranceProgramsPayload {
  items: Array<{
    productVersionId: string | null;
    lineOfBusiness: string | null;
  }>;
}

export interface InsuranceCession {
  id: string;
  treatyId: string;
  policyId: string | null;
  claimId: string | null;
  cededPremium: number;
  cededLoss: number;
  accountingPeriod: string;
  sourceEventType: string;
}

export interface CalculateInsuranceCessionsPayload {
  accountingPeriod: string;
  treatyId: string | null;
  policyId: string | null;
  claimId: string | null;
}

export interface InsuranceCessionCalculationResult {
  accountingPeriod: string;
  processedCount: number;
  totalCededPremium: number;
  totalCededLoss: number;
  cessions: InsuranceCession[];
}

export interface InsuranceReinsuranceBordereau {
  id: string;
  period: string;
  treatyId: string;
  status: 'Generated' | 'Exported' | string;
  exportFileId: string | null;
  totalCededPremium: number;
  totalCededLoss: number;
  itemCount: number;
  generatedAtUtc: string;
  exportedAtUtc: string | null;
}

export interface CreateInsuranceReinsuranceBordereauPayload {
  period: string;
  treatyId: string;
}

export interface InsuranceReinsuranceBordereauExport {
  bordereauId: string;
  fileName: string;
  contentType: string;
  contentBase64: string;
  exportedAtUtc: string;
  itemCount: number;
  totalCededPremium: number;
  totalCededLoss: number;
}

export interface InsuranceRegulatoryReportDefinitionListItem {
  id: string;
  code: string;
  name: string;
  jurisdiction: 'IR' | 'EU' | 'Custom' | string;
  schemaVersion: string;
  isActive: boolean;
}

export interface InsuranceRegulatoryReportDefinitionDetail extends InsuranceRegulatoryReportDefinitionListItem {
  legalEntityId: string;
  templateJson: string;
}

export interface CreateInsuranceRegulatoryDefinitionPayload {
  legalEntityId: string;
  code: string;
  name: string;
  jurisdiction: string;
  schemaVersion: string;
  templateJson: string | null;
}

export interface UpdateInsuranceRegulatoryDefinitionPayload {
  name: string;
  jurisdiction: string;
  schemaVersion: string;
  templateJson: string | null;
  isActive: boolean;
}

export interface InsuranceRegulatoryValidationRule {
  id: string;
  definitionId: string;
  ruleCode: string;
  fieldPath: string;
  isRequired: boolean;
  errorCode: string;
  errorMessage: string;
  isActive: boolean;
}

export interface PutInsuranceRegulatoryValidationRulesPayload {
  items: Array<{
    ruleCode: string;
    fieldPath: string;
    isRequired: boolean;
    errorCode: string;
    errorMessage: string;
    isActive: boolean;
  }>;
}

export interface InsuranceRegulatoryReportJobListItem {
  id: string;
  definitionId: string;
  definitionCode: string;
  definitionName: string;
  jurisdiction: string;
  periodFrom: string;
  periodTo: string;
  status: 'Pending' | 'Validating' | 'Ready' | 'Submitted' | 'Failed' | string;
  createdAtUtc: string;
  submittedAtUtc: string | null;
}

export interface InsuranceRegulatoryReportJobDetail extends InsuranceRegulatoryReportJobListItem {
  payloadFileId: string | null;
  validationResultJson: string;
  lastError: string | null;
}

export interface CreateInsuranceRegulatoryReportJobPayload {
  definitionId: string;
  periodFrom: string;
  periodTo: string;
}

export interface ValidateInsuranceRegulatoryReportJobPayload {
  payloadJson: string | null;
}

export interface InsuranceRegulatoryValidationIssue {
  ruleCode: string;
  errorCode: string;
  message: string;
}

export interface InsuranceRegulatoryJobValidationResult {
  jobId: string;
  status: string;
  isValid: boolean;
  payloadFileId: string;
  issues: InsuranceRegulatoryValidationIssue[];
}

export interface SubmitInsuranceRegulatoryReportJobPayload {
  force: boolean;
}

export interface InsuranceRegulatoryJobSubmissionResult {
  jobId: string;
  status: string;
  externalReference: string;
  submittedAtUtc: string;
}

export interface InsuranceRegulatoryJobDownloadResult {
  jobId: string;
  fileName: string;
  contentType: string;
  contentBase64: string;
}

export interface SyncInsuranceSanhabPolicyPayload {
  force: boolean;
  payloadJson: string | null;
}

export interface InsuranceSanhabSubmission {
  id: string;
  policyId: string | null;
  regulatoryJobId: string | null;
  externalReference: string;
  status: string;
  requestPayloadHash: string;
  responseJson: string;
  createdAtUtc: string;
}

const FALLBACK_POLICIES: InsurancePolicyListItem[] = [
  {
    id: 'a8e938f0-ec52-45ad-a11d-8e3a3f44e101',
    policyNumber: 'POL-1405-00031',
    productName: 'Motor Third Party',
    lineOfBusiness: 'Motor',
    status: 'InForce',
    premiumAmount: 185000000,
    currencyCode: 'IRR',
    effectiveFromUtc: '2026-05-01T00:00:00Z',
    effectiveToUtc: '2027-05-01T00:00:00Z'
  },
  {
    id: '6d6d27f2-a4bb-47f1-a4f8-a23d57d2230e',
    policyNumber: 'POL-1405-00044',
    productName: 'Family Health Plus',
    lineOfBusiness: 'Health',
    status: 'Suspended',
    premiumAmount: 420000000,
    currencyCode: 'IRR',
    effectiveFromUtc: '2026-04-01T00:00:00Z',
    effectiveToUtc: '2027-04-01T00:00:00Z'
  },
  {
    id: '3b26295f-e5f3-4be5-ab98-f24a6d5cc8d0',
    policyNumber: 'POL-1405-00052',
    productName: 'Property Fire Shield',
    lineOfBusiness: 'Property',
    status: 'Draft',
    premiumAmount: 76000000,
    currencyCode: 'IRR',
    effectiveFromUtc: '2026-06-15T00:00:00Z',
    effectiveToUtc: '2027-06-15T00:00:00Z'
  }
];

const FALLBACK_CLAIMS: InsuranceClaimListItem[] = [
  {
    id: '4f82f9cb-124d-40ad-a94a-f5d7483d9b30',
    claimNumber: 'CLM-1405-00011',
    policyNumber: 'POL-1405-00031',
    producerId: null,
    lineOfBusiness: 'Motor',
    status: 'UnderInvestigation',
    reserveAmount: 35000000,
    paidAmount: 12000000,
    currencyCode: 'IRR',
    openedAtUtc: '2026-05-21T08:15:00Z',
    lossDateUtc: '2026-05-20T15:20:00Z',
    fnolChannel: 'Portal'
  },
  {
    id: '4f19a22a-241d-4fb2-a565-43e8762e9811',
    claimNumber: 'CLM-1405-00018',
    policyNumber: 'POL-1405-00044',
    producerId: null,
    lineOfBusiness: 'Health',
    status: 'Reported',
    reserveAmount: 12500000,
    paidAmount: 0,
    currencyCode: 'IRR',
    openedAtUtc: '2026-06-01T11:20:00Z',
    lossDateUtc: '2026-05-31T09:10:00Z',
    fnolChannel: 'CallCenter'
  },
  {
    id: 'ff01d4c0-4888-4f4e-8f93-1224bfd21462',
    claimNumber: 'CLM-1405-00005',
    policyNumber: 'POL-1405-00020',
    producerId: null,
    lineOfBusiness: 'Property',
    status: 'Paid',
    reserveAmount: 9100000,
    paidAmount: 9100000,
    currencyCode: 'IRR',
    openedAtUtc: '2026-04-16T13:40:00Z',
    lossDateUtc: '2026-04-15T18:20:00Z',
    fnolChannel: 'Agent'
  }
];

@Injectable({ providedIn: 'root' })
export class InsuranceApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/insurance`;

  listPolicies(): Observable<InsurancePolicyListItem[]> {
    return this.http.get<InsurancePolicyListItem[]>(`${this.base}/policies`).pipe(
      catchError(() => of(FALLBACK_POLICIES))
    );
  }

  listQuotes(): Observable<InsuranceQuoteListItem[]> {
    return this.http.get<InsuranceQuoteListItem[]>(`${this.base}/quotes`).pipe(
      catchError(() => of([]))
    );
  }

  listProducers(status?: string): Observable<InsuranceProducerListItem[]> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.http.get<InsuranceProducerListItem[]>(`${this.base}/producers${query}`).pipe(
      catchError(() => of([]))
    );
  }

  getProducer(id: string): Observable<InsuranceProducerDetail | null> {
    return this.http.get<InsuranceProducerDetail>(`${this.base}/producers/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createProducer(payload: CreateInsuranceProducerPayload): Observable<InsuranceProducerDetail> {
    return this.http.post<InsuranceProducerDetail>(`${this.base}/producers`, payload);
  }

  updateProducer(id: string, payload: UpdateInsuranceProducerPayload): Observable<InsuranceProducerDetail> {
    return this.http.put<InsuranceProducerDetail>(`${this.base}/producers/${id}`, payload);
  }

  listProducerAgreements(id: string): Observable<InsuranceProducerAgreement[]> {
    return this.http.get<InsuranceProducerAgreement[]>(`${this.base}/producers/${id}/agreements`).pipe(
      catchError(() => of([]))
    );
  }

  upsertProducerAgreements(id: string, payload: UpsertInsuranceProducerAgreementsPayload): Observable<InsuranceProducerAgreement[]> {
    return this.http.put<InsuranceProducerAgreement[]>(`${this.base}/producers/${id}/agreements`, payload);
  }

  validateProducerLicense(id: string): Observable<InsuranceProducerLicenseValidation> {
    return this.http.post<InsuranceProducerLicenseValidation>(`${this.base}/producers/${id}/validate-license`, {});
  }

  listAgencies(): Observable<InsuranceAgencyListItem[]> {
    return this.http.get<InsuranceAgencyListItem[]>(`${this.base}/agencies`).pipe(
      catchError(() => of([]))
    );
  }

  getAgency(id: string): Observable<InsuranceAgencyDetail | null> {
    return this.http.get<InsuranceAgencyDetail>(`${this.base}/agencies/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createAgency(payload: CreateInsuranceAgencyPayload): Observable<InsuranceAgencyDetail> {
    return this.http.post<InsuranceAgencyDetail>(`${this.base}/agencies`, payload);
  }

  updateAgency(id: string, payload: UpdateInsuranceAgencyPayload): Observable<InsuranceAgencyDetail> {
    return this.http.put<InsuranceAgencyDetail>(`${this.base}/agencies/${id}`, payload);
  }

  listTerritories(): Observable<InsuranceTerritory[]> {
    return this.http.get<InsuranceTerritory[]>(`${this.base}/territories`).pipe(
      catchError(() => of([]))
    );
  }

  createTerritory(payload: CreateInsuranceTerritoryPayload): Observable<InsuranceTerritory> {
    return this.http.post<InsuranceTerritory>(`${this.base}/territories`, payload);
  }

  updateTerritory(id: string, payload: UpdateInsuranceTerritoryPayload): Observable<InsuranceTerritory> {
    return this.http.put<InsuranceTerritory>(`${this.base}/territories/${id}`, payload);
  }

  listHealthProviders(filter?: { contractStatus?: string | null; providerType?: string | null }): Observable<InsuranceProviderListItem[]> {
    const params = new URLSearchParams();
    if (filter?.contractStatus) {
      params.set('contractStatus', filter.contractStatus);
    }
    if (filter?.providerType) {
      params.set('providerType', filter.providerType);
    }
    const query = params.size > 0 ? `?${params.toString()}` : '';
    return this.http.get<InsuranceProviderListItem[]>(`${this.base}/health/providers${query}`).pipe(
      catchError(() => of([]))
    );
  }

  getHealthProvider(id: string): Observable<InsuranceProviderDetail | null> {
    return this.http.get<InsuranceProviderDetail>(`${this.base}/health/providers/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createHealthProvider(payload: CreateInsuranceProviderPayload): Observable<InsuranceProviderDetail> {
    return this.http.post<InsuranceProviderDetail>(`${this.base}/health/providers`, payload);
  }

  updateHealthProvider(id: string, payload: UpdateInsuranceProviderPayload): Observable<InsuranceProviderDetail> {
    return this.http.put<InsuranceProviderDetail>(`${this.base}/health/providers/${id}`, payload);
  }

  listHealthProviderContracts(providerId: string): Observable<InsuranceProviderContract[]> {
    return this.http.get<InsuranceProviderContract[]>(`${this.base}/health/providers/${providerId}/contracts`).pipe(
      catchError(() => of([]))
    );
  }

  upsertHealthProviderContracts(providerId: string, payload: UpsertInsuranceProviderContractsPayload): Observable<InsuranceProviderContract[]> {
    return this.http.put<InsuranceProviderContract[]>(`${this.base}/health/providers/${providerId}/contracts`, payload);
  }

  listPolicyHealthEligibility(policyId: string): Observable<InsuranceMemberEligibility[]> {
    return this.http.get<InsuranceMemberEligibility[]>(`${this.base}/policies/${policyId}/health-eligibility`).pipe(
      catchError(() => of([]))
    );
  }

  upsertPolicyHealthEligibility(policyId: string, payload: UpsertInsuranceMemberEligibilityPayload): Observable<InsuranceMemberEligibility[]> {
    return this.http.put<InsuranceMemberEligibility[]>(`${this.base}/policies/${policyId}/health-eligibility`, payload);
  }

  checkHealthEligibility(payload: CheckInsuranceEligibilityPayload): Observable<InsuranceEligibilityCheckResult> {
    return this.http.post<InsuranceEligibilityCheckResult>(`${this.base}/health/eligibility/check`, payload);
  }

  createHealthPreAuthorization(payload: CreateInsurancePreAuthorizationPayload): Observable<InsurancePreAuthorization> {
    return this.http.post<InsurancePreAuthorization>(`${this.base}/health/pre-auth`, payload);
  }

  listMasterPolicies(): Observable<InsuranceMasterPolicyListItem[]> {
    return this.http.get<InsuranceMasterPolicyListItem[]>(`${this.base}/master-policies`).pipe(
      catchError(() => of([]))
    );
  }

  getMasterPolicy(id: string): Observable<InsuranceMasterPolicyDetail | null> {
    return this.http.get<InsuranceMasterPolicyDetail>(`${this.base}/master-policies/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createMasterPolicy(payload: CreateInsuranceMasterPolicyPayload): Observable<InsuranceMasterPolicyDetail> {
    return this.http.post<InsuranceMasterPolicyDetail>(`${this.base}/master-policies`, payload);
  }

  updateMasterPolicy(id: string, payload: UpdateInsuranceMasterPolicyPayload): Observable<InsuranceMasterPolicyDetail> {
    return this.http.put<InsuranceMasterPolicyDetail>(`${this.base}/master-policies/${id}`, payload);
  }

  deleteMasterPolicy(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/master-policies/${id}`);
  }

  addMasterPolicyMember(id: string, payload: AddInsuranceMemberCertificatePayload): Observable<InsuranceMemberCertificate> {
    return this.http.post<InsuranceMemberCertificate>(`${this.base}/master-policies/${id}/members`, payload);
  }

  createMasterPolicyBulkChange(id: string, payload: CreateInsuranceBulkChangeBatchPayload): Observable<InsuranceBulkChangeBatch> {
    return this.http.post<InsuranceBulkChangeBatch>(`${this.base}/master-policies/${id}/bulk-changes`, payload);
  }

  approveMasterPolicyBulkChange(id: string, batchId: string): Observable<InsuranceBulkChangeBatch> {
    return this.http.post<InsuranceBulkChangeBatch>(`${this.base}/master-policies/${id}/bulk-changes/${batchId}/approve`, {});
  }

  applyMasterPolicyBulkChange(id: string, batchId: string): Observable<InsuranceBulkChangeBatch> {
    return this.http.post<InsuranceBulkChangeBatch>(`${this.base}/master-policies/${id}/bulk-changes/${batchId}/apply`, {});
  }

  getMasterPolicyCensusReport(id: string): Observable<InsuranceCensusReport | null> {
    return this.http.get<InsuranceCensusReport>(`${this.base}/master-policies/${id}/census-report`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  listReinsuranceTreaties(): Observable<InsuranceReinsuranceTreatyListItem[]> {
    return this.http.get<InsuranceReinsuranceTreatyListItem[]>(`${this.base}/reinsurance/treaties`).pipe(
      catchError(() => of([]))
    );
  }

  getReinsuranceTreaty(id: string): Observable<InsuranceReinsuranceTreatyDetail | null> {
    return this.http.get<InsuranceReinsuranceTreatyDetail>(`${this.base}/reinsurance/treaties/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createReinsuranceTreaty(payload: CreateInsuranceReinsuranceTreatyPayload): Observable<InsuranceReinsuranceTreatyDetail> {
    return this.http.post<InsuranceReinsuranceTreatyDetail>(`${this.base}/reinsurance/treaties`, payload);
  }

  updateReinsuranceTreaty(id: string, payload: UpdateInsuranceReinsuranceTreatyPayload): Observable<InsuranceReinsuranceTreatyDetail> {
    return this.http.put<InsuranceReinsuranceTreatyDetail>(`${this.base}/reinsurance/treaties/${id}`, payload);
  }

  putReinsurancePrograms(id: string, payload: PutInsuranceReinsuranceProgramsPayload): Observable<InsuranceReinsuranceProgram[]> {
    return this.http.put<InsuranceReinsuranceProgram[]>(`${this.base}/reinsurance/treaties/${id}/programs`, payload);
  }

  deleteReinsuranceTreaty(id: string): Observable<void> {
    return this.http.delete<void>(`${this.base}/reinsurance/treaties/${id}`);
  }

  listReinsuranceCessions(filter?: {
    accountingPeriod?: string | null;
    treatyId?: string | null;
    policyId?: string | null;
    claimId?: string | null;
  }): Observable<InsuranceCession[]> {
    const params = new URLSearchParams();
    if (filter?.accountingPeriod) {
      params.set('accountingPeriod', filter.accountingPeriod);
    }
    if (filter?.treatyId) {
      params.set('treatyId', filter.treatyId);
    }
    if (filter?.policyId) {
      params.set('policyId', filter.policyId);
    }
    if (filter?.claimId) {
      params.set('claimId', filter.claimId);
    }
    const query = params.size > 0 ? `?${params.toString()}` : '';
    return this.http.get<InsuranceCession[]>(`${this.base}/reinsurance/cessions${query}`).pipe(
      catchError(() => of([]))
    );
  }

  calculateReinsuranceCessions(payload: CalculateInsuranceCessionsPayload): Observable<InsuranceCessionCalculationResult> {
    return this.http.post<InsuranceCessionCalculationResult>(`${this.base}/reinsurance/cessions/calculate`, payload);
  }

  listReinsuranceBordereaux(filter?: {
    period?: string | null;
    treatyId?: string | null;
  }): Observable<InsuranceReinsuranceBordereau[]> {
    const params = new URLSearchParams();
    if (filter?.period) {
      params.set('period', filter.period);
    }
    if (filter?.treatyId) {
      params.set('treatyId', filter.treatyId);
    }
    const query = params.size > 0 ? `?${params.toString()}` : '';
    return this.http.get<InsuranceReinsuranceBordereau[]>(`${this.base}/reinsurance/bordereaux${query}`).pipe(
      catchError(() => of([]))
    );
  }

  createReinsuranceBordereau(payload: CreateInsuranceReinsuranceBordereauPayload): Observable<InsuranceReinsuranceBordereau> {
    return this.http.post<InsuranceReinsuranceBordereau>(`${this.base}/reinsurance/bordereaux`, payload);
  }

  exportReinsuranceBordereau(id: string): Observable<InsuranceReinsuranceBordereauExport> {
    return this.http.get<InsuranceReinsuranceBordereauExport>(`${this.base}/reinsurance/bordereaux/${id}/export`);
  }

  listRegulatoryDefinitions(): Observable<InsuranceRegulatoryReportDefinitionListItem[]> {
    return this.http.get<InsuranceRegulatoryReportDefinitionListItem[]>(`${this.base}/regulatory/definitions`).pipe(
      catchError(() => of([]))
    );
  }

  getRegulatoryDefinition(id: string): Observable<InsuranceRegulatoryReportDefinitionDetail | null> {
    return this.http.get<InsuranceRegulatoryReportDefinitionDetail>(`${this.base}/regulatory/definitions/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createRegulatoryDefinition(payload: CreateInsuranceRegulatoryDefinitionPayload): Observable<InsuranceRegulatoryReportDefinitionDetail> {
    return this.http.post<InsuranceRegulatoryReportDefinitionDetail>(`${this.base}/regulatory/definitions`, payload);
  }

  updateRegulatoryDefinition(id: string, payload: UpdateInsuranceRegulatoryDefinitionPayload): Observable<InsuranceRegulatoryReportDefinitionDetail> {
    return this.http.put<InsuranceRegulatoryReportDefinitionDetail>(`${this.base}/regulatory/definitions/${id}`, payload);
  }

  listRegulatoryValidationRules(definitionId: string): Observable<InsuranceRegulatoryValidationRule[]> {
    return this.http.get<InsuranceRegulatoryValidationRule[]>(`${this.base}/regulatory/definitions/${definitionId}/validation-rules`).pipe(
      catchError(() => of([]))
    );
  }

  putRegulatoryValidationRules(
    definitionId: string,
    payload: PutInsuranceRegulatoryValidationRulesPayload
  ): Observable<InsuranceRegulatoryValidationRule[]> {
    return this.http.put<InsuranceRegulatoryValidationRule[]>(`${this.base}/regulatory/definitions/${definitionId}/validation-rules`, payload);
  }

  listRegulatoryJobs(filter?: {
    definitionId?: string | null;
    status?: string | null;
  }): Observable<InsuranceRegulatoryReportJobListItem[]> {
    const params = new URLSearchParams();
    if (filter?.definitionId) {
      params.set('definitionId', filter.definitionId);
    }
    if (filter?.status) {
      params.set('status', filter.status);
    }
    const query = params.size > 0 ? `?${params.toString()}` : '';
    return this.http.get<InsuranceRegulatoryReportJobListItem[]>(`${this.base}/regulatory/jobs${query}`).pipe(
      catchError(() => of([]))
    );
  }

  getRegulatoryJob(jobId: string): Observable<InsuranceRegulatoryReportJobDetail | null> {
    return this.http.get<InsuranceRegulatoryReportJobDetail>(`${this.base}/regulatory/jobs/${jobId}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createRegulatoryJob(payload: CreateInsuranceRegulatoryReportJobPayload): Observable<InsuranceRegulatoryReportJobDetail> {
    return this.http.post<InsuranceRegulatoryReportJobDetail>(`${this.base}/regulatory/jobs`, payload);
  }

  validateRegulatoryJob(
    jobId: string,
    payload: ValidateInsuranceRegulatoryReportJobPayload
  ): Observable<InsuranceRegulatoryJobValidationResult> {
    return this.http.post<InsuranceRegulatoryJobValidationResult>(`${this.base}/regulatory/jobs/${jobId}/validate`, payload);
  }

  submitRegulatoryJob(
    jobId: string,
    payload: SubmitInsuranceRegulatoryReportJobPayload
  ): Observable<InsuranceRegulatoryJobSubmissionResult> {
    return this.http.post<InsuranceRegulatoryJobSubmissionResult>(`${this.base}/regulatory/jobs/${jobId}/submit`, payload);
  }

  downloadRegulatoryJob(jobId: string): Observable<InsuranceRegulatoryJobDownloadResult> {
    return this.http.get<InsuranceRegulatoryJobDownloadResult>(`${this.base}/regulatory/jobs/${jobId}/download`);
  }

  syncSanhabPolicy(
    policyId: string,
    payload: SyncInsuranceSanhabPolicyPayload
  ): Observable<InsuranceSanhabSubmission> {
    return this.http.post<InsuranceSanhabSubmission>(`${this.base}/sanhab/policies/${policyId}/sync`, payload);
  }

  getChannelBinding(salesChannelId: string): Observable<InsuranceChannelBinding | null> {
    return this.http.get<InsuranceChannelBinding>(`${this.base}/channel-bindings/${salesChannelId}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  upsertChannelBinding(salesChannelId: string, payload: UpsertInsuranceChannelBindingPayload): Observable<InsuranceChannelBinding> {
    return this.http.put<InsuranceChannelBinding>(`${this.base}/channel-bindings/${salesChannelId}`, payload);
  }

  listCommissionPlans(isActive?: boolean): Observable<InsuranceCommissionPlanListItem[]> {
    const query = typeof isActive === 'boolean' ? `?isActive=${isActive}` : '';
    return this.http.get<InsuranceCommissionPlanListItem[]>(`${this.base}/commission-plans${query}`).pipe(
      catchError(() => of([]))
    );
  }

  getCommissionPlan(id: string): Observable<InsuranceCommissionPlanDetail | null> {
    return this.http.get<InsuranceCommissionPlanDetail>(`${this.base}/commission-plans/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createCommissionPlan(payload: CreateInsuranceCommissionPlanPayload): Observable<InsuranceCommissionPlanDetail> {
    return this.http.post<InsuranceCommissionPlanDetail>(`${this.base}/commission-plans`, payload);
  }

  updateCommissionPlan(id: string, payload: UpdateInsuranceCommissionPlanPayload): Observable<InsuranceCommissionPlanDetail> {
    return this.http.put<InsuranceCommissionPlanDetail>(`${this.base}/commission-plans/${id}`, payload);
  }

  listCommissionAccruals(producerId?: string | null, period?: string | null): Observable<InsuranceCommissionAccrual[]> {
    const params = new URLSearchParams();
    if (producerId) {
      params.set('producerId', producerId);
    }
    if (period) {
      params.set('period', period);
    }
    const query = params.size > 0 ? `?${params.toString()}` : '';
    return this.http.get<InsuranceCommissionAccrual[]>(`${this.base}/commission-accruals${query}`).pipe(
      catchError(() => of([]))
    );
  }

  listCommissionSettlements(producerId?: string | null): Observable<InsuranceCommissionSettlementListItem[]> {
    const query = producerId ? `?producerId=${encodeURIComponent(producerId)}` : '';
    return this.http.get<InsuranceCommissionSettlementListItem[]>(`${this.base}/commission-settlements${query}`).pipe(
      catchError(() => of([]))
    );
  }

  getCommissionSettlement(id: string): Observable<InsuranceCommissionSettlementDetail | null> {
    return this.http.get<InsuranceCommissionSettlementDetail>(`${this.base}/commission-settlements/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createCommissionSettlement(payload: CreateInsuranceCommissionSettlementPayload): Observable<InsuranceCommissionSettlementDetail> {
    return this.http.post<InsuranceCommissionSettlementDetail>(`${this.base}/commission-settlements`, payload);
  }

  approveCommissionSettlement(id: string): Observable<InsuranceCommissionSettlementDetail> {
    return this.http.post<InsuranceCommissionSettlementDetail>(`${this.base}/commission-settlements/${id}/approve`, {});
  }

  payCommissionSettlement(id: string, payload: PayInsuranceCommissionSettlementPayload): Observable<InsuranceCommissionSettlementDetail> {
    return this.http.post<InsuranceCommissionSettlementDetail>(`${this.base}/commission-settlements/${id}/pay`, payload);
  }

  getPolicyPremiumSchedule(policyId: string): Observable<InsurancePremiumSchedule | null> {
    return this.http.get<InsurancePremiumSchedule>(`${this.base}/policies/${policyId}/premium-schedule`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createPolicyPremiumSchedule(policyId: string, payload: CreateInsurancePremiumSchedulePayload): Observable<InsurancePremiumSchedule> {
    return this.http.post<InsurancePremiumSchedule>(`${this.base}/policies/${policyId}/premium-schedule`, payload);
  }

  listPremiumInvoices(filter?: {
    producerId?: string | null;
    lineOfBusiness?: string | null;
    status?: string | null;
  }): Observable<InsurancePremiumInvoiceListItem[]> {
    const params = new URLSearchParams();
    if (filter?.producerId) {
      params.set('producerId', filter.producerId);
    }
    if (filter?.lineOfBusiness) {
      params.set('lineOfBusiness', filter.lineOfBusiness);
    }
    if (filter?.status) {
      params.set('status', filter.status);
    }
    const query = params.size > 0 ? `?${params.toString()}` : '';
    return this.http.get<InsurancePremiumInvoiceListItem[]>(`${this.base}/premium-invoices${query}`).pipe(
      catchError(() => of([]))
    );
  }

  getPremiumInvoice(id: string): Observable<InsurancePremiumInvoiceDetail | null> {
    return this.http.get<InsurancePremiumInvoiceDetail>(`${this.base}/premium-invoices/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  collectPremiumInvoice(id: string, payload: CollectInsurancePremiumInvoicePayload): Observable<InsurancePremiumInvoiceDetail> {
    return this.http.post<InsurancePremiumInvoiceDetail>(`${this.base}/premium-invoices/${id}/collect`, payload);
  }

  writeOffPremiumInvoice(id: string, reason: string | null): Observable<InsurancePremiumInvoiceDetail> {
    return this.http.post<InsurancePremiumInvoiceDetail>(`${this.base}/premium-invoices/${id}/write-off`, { reason });
  }

  runPremiumEarningRun(payload: RunInsurancePremiumEarningPayload): Observable<InsurancePremiumEarningRunBatch> {
    return this.http.post<InsurancePremiumEarningRunBatch>(`${this.base}/premium-earning-runs`, payload);
  }

  getQuote(id: string): Observable<InsuranceQuoteDetail | null> {
    return this.http.get<InsuranceQuoteDetail>(`${this.base}/quotes/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createQuote(payload: CreateInsuranceQuotePayload): Observable<InsuranceQuoteDetail> {
    return this.http.post<InsuranceQuoteDetail>(`${this.base}/quotes`, payload);
  }

  priceQuote(id: string, payload: PriceInsuranceQuotePayload): Observable<InsuranceQuoteDetail> {
    return this.http.post<InsuranceQuoteDetail>(`${this.base}/quotes/${id}/price`, payload);
  }

  createQuoteOptions(id: string, options: CreateInsuranceQuoteOptionPayload[]): Observable<InsuranceQuoteDetail> {
    return this.http.post<InsuranceQuoteDetail>(`${this.base}/quotes/${id}/options`, { options });
  }

  convertQuoteToProposal(id: string, payload: ConvertQuoteToProposalPayload): Observable<InsuranceProposalDetail> {
    return this.http.post<InsuranceProposalDetail>(`${this.base}/quotes/${id}/convert-to-proposal`, payload);
  }

  listProposals(): Observable<InsuranceProposalListItem[]> {
    return this.http.get<InsuranceProposalListItem[]>(`${this.base}/proposals`).pipe(
      catchError(() => of([]))
    );
  }

  getProposal(id: string): Observable<InsuranceProposalDetail | null> {
    return this.http.get<InsuranceProposalDetail>(`${this.base}/proposals/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createProposal(payload: CreateInsuranceProposalPayload): Observable<InsuranceProposalDetail> {
    return this.http.post<InsuranceProposalDetail>(`${this.base}/proposals`, payload);
  }

  submitProposal(id: string): Observable<InsuranceProposalDetail> {
    return this.http.post<InsuranceProposalDetail>(`${this.base}/proposals/${id}/submit`, {});
  }

  cancelProposal(id: string): Observable<InsuranceProposalDetail> {
    return this.http.post<InsuranceProposalDetail>(`${this.base}/proposals/${id}/cancel`, {});
  }

  bindProposal(id: string, payload: BindInsuranceProposalPayload, idempotencyKey: string): Observable<InsuranceProposalDetail> {
    return this.http.post<InsuranceProposalDetail>(`${this.base}/proposals/${id}/bind`, payload, {
      headers: { 'Idempotency-Key': idempotencyKey }
    });
  }

  listProductVersions(): Observable<InsuranceProductVersionCatalogListItem[]> {
    return this.http.get<InsuranceProductVersionCatalogListItem[]>(`${this.base}/product-versions`).pipe(
      catchError(() => of([]))
    );
  }

  listProducts(): Observable<InsuranceProductListItem[]> {
    return this.http.get<InsuranceProductListItem[]>(`${this.base}/products`).pipe(
      catchError(() => of([]))
    );
  }

  getProduct(id: string): Observable<InsuranceProductDetail | null> {
    return this.http.get<InsuranceProductDetail>(`${this.base}/products/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createProduct(payload: CreateInsuranceProductPayload): Observable<InsuranceProductDetail> {
    return this.http.post<InsuranceProductDetail>(`${this.base}/products`, payload);
  }

  updateProduct(id: string, payload: UpdateInsuranceProductPayload): Observable<InsuranceProductDetail> {
    return this.http.put<InsuranceProductDetail>(`${this.base}/products/${id}`, payload);
  }

  getProductVersion(productId: string, versionId: string): Observable<InsuranceProductVersionDetail | null> {
    return this.http.get<InsuranceProductVersionDetail>(`${this.base}/products/${productId}/versions/${versionId}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createProductVersion(productId: string, payload: CreateInsuranceProductVersionPayload): Observable<InsuranceProductVersionDetail> {
    return this.http.post<InsuranceProductVersionDetail>(`${this.base}/products/${productId}/versions`, payload);
  }

  updateProductVersion(
    productId: string,
    versionId: string,
    payload: UpdateInsuranceProductVersionPayload
  ): Observable<InsuranceProductVersionDetail> {
    return this.http.put<InsuranceProductVersionDetail>(`${this.base}/products/${productId}/versions/${versionId}`, payload);
  }

  publishProductVersion(productId: string, versionId: string): Observable<InsuranceProductVersionDetail> {
    return this.http.post<InsuranceProductVersionDetail>(`${this.base}/products/${productId}/versions/${versionId}/publish`, {});
  }

  retireProductVersion(productId: string, versionId: string): Observable<InsuranceProductVersionDetail> {
    return this.http.post<InsuranceProductVersionDetail>(`${this.base}/products/${productId}/versions/${versionId}/retire`, {});
  }

  listVersionCoverages(productId: string, versionId: string): Observable<InsuranceCoverageItem[]> {
    return this.http.get<InsuranceCoverageItem[]>(`${this.base}/products/${productId}/versions/${versionId}/coverages`).pipe(
      catchError(() => of([]))
    );
  }

  upsertVersionCoverages(
    productId: string,
    versionId: string,
    payload: UpsertInsuranceCoveragesPayload
  ): Observable<InsuranceCoverageItem[]> {
    return this.http.put<InsuranceCoverageItem[]>(`${this.base}/products/${productId}/versions/${versionId}/coverages`, payload);
  }

  listVersionClauses(productId: string, versionId: string): Observable<InsuranceClauseItem[]> {
    return this.http.get<InsuranceClauseItem[]>(`${this.base}/products/${productId}/versions/${versionId}/clauses`).pipe(
      catchError(() => of([]))
    );
  }

  upsertVersionClauses(
    productId: string,
    versionId: string,
    payload: UpsertInsuranceClausesPayload
  ): Observable<InsuranceClauseItem[]> {
    return this.http.put<InsuranceClauseItem[]>(`${this.base}/products/${productId}/versions/${versionId}/clauses`, payload);
  }

  listVersionQuestions(productId: string, versionId: string): Observable<InsuranceQuestionItem[]> {
    return this.http.get<InsuranceQuestionItem[]>(`${this.base}/products/${productId}/versions/${versionId}/questions`).pipe(
      catchError(() => of([]))
    );
  }

  upsertVersionQuestions(
    productId: string,
    versionId: string,
    payload: UpsertInsuranceQuestionsPayload
  ): Observable<InsuranceQuestionItem[]> {
    return this.http.put<InsuranceQuestionItem[]>(`${this.base}/products/${productId}/versions/${versionId}/questions`, payload);
  }

  listUwRules(productId: string, versionId: string): Observable<InsuranceUwRule[]> {
    return this.http.get<InsuranceUwRule[]>(`${this.base}/products/${productId}/versions/${versionId}/uw-rules`).pipe(
      catchError(() => of([]))
    );
  }

  putUwRules(
    productId: string,
    versionId: string,
    items: PutInsuranceUwRuleItemPayload[]
  ): Observable<InsuranceUwRule[]> {
    return this.http.put<InsuranceUwRule[]>(`${this.base}/products/${productId}/versions/${versionId}/uw-rules`, { items });
  }

  listUwCases(status?: string): Observable<InsuranceUwCaseListItem[]> {
    const url = status ? `${this.base}/uw-cases?status=${encodeURIComponent(status)}` : `${this.base}/uw-cases`;
    return this.http.get<InsuranceUwCaseListItem[]>(url).pipe(
      catchError(() => of([]))
    );
  }

  getUwCase(id: string): Observable<InsuranceUwCaseDetail | null> {
    return this.http.get<InsuranceUwCaseDetail>(`${this.base}/uw-cases/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  approveUwCase(id: string, payload: ApproveInsuranceUwCasePayload): Observable<InsuranceUwCaseDetail> {
    return this.http.post<InsuranceUwCaseDetail>(`${this.base}/uw-cases/${id}/approve`, payload);
  }

  declineUwCase(id: string, payload: DeclineInsuranceUwCasePayload): Observable<InsuranceUwCaseDetail> {
    return this.http.post<InsuranceUwCaseDetail>(`${this.base}/uw-cases/${id}/decline`, payload);
  }

  referUwCase(id: string, payload: ReferInsuranceUwCasePayload): Observable<InsuranceUwCaseDetail> {
    return this.http.post<InsuranceUwCaseDetail>(`${this.base}/uw-cases/${id}/refer`, payload);
  }

  requestUwCaseInfo(id: string, payload: RequestInfoInsuranceUwCasePayload): Observable<InsuranceUwCaseDetail> {
    return this.http.post<InsuranceUwCaseDetail>(`${this.base}/uw-cases/${id}/request-info`, payload);
  }

  validateProductVersion(productId: string, versionId: string): Observable<InsuranceVersionValidationResult> {
    return this.http.post<InsuranceVersionValidationResult>(`${this.base}/products/${productId}/versions/${versionId}/validate`, {});
  }

  exportProductVersion(productId: string, versionId: string): Observable<InsuranceVersionExportBundle> {
    return this.http.get<InsuranceVersionExportBundle>(`${this.base}/products/${productId}/versions/${versionId}/export`);
  }

  listRatingPlans(): Observable<InsuranceRatingPlan[]> {
    return this.http.get<InsuranceRatingPlan[]>(`${this.base}/rating-plans`).pipe(
      catchError(() => of([]))
    );
  }

  upsertRatingPlan(payload: UpsertInsuranceRatingPlanPayload): Observable<InsuranceRatingPlan> {
    return this.http.put<InsuranceRatingPlan>(`${this.base}/rating-plans`, payload);
  }

  calculatePremium(payload: InsuranceRatingCalculatePayload): Observable<InsurancePremiumBreakdown> {
    return this.http.post<InsurancePremiumBreakdown>(`${this.base}/rating/calculate`, payload);
  }

  repricePremium(payload: InsuranceRatingRepricePayload): Observable<InsurancePremiumBreakdown> {
    return this.http.post<InsurancePremiumBreakdown>(`${this.base}/rating/reprice`, payload);
  }

  getRatingPlanTables(ratingPlanId: string): Observable<InsuranceRatingPlanTables> {
    return this.http.get<InsuranceRatingPlanTables>(`${this.base}/rating-plans/${ratingPlanId}/tables`);
  }

  updateRatingPlanTables(ratingPlanId: string, rateTableJson: string): Observable<InsuranceRatingPlanTables> {
    return this.http.put<InsuranceRatingPlanTables>(`${this.base}/rating-plans/${ratingPlanId}/tables`, { rateTableJson });
  }

  simulateRatingPlan(
    ratingPlanId: string,
    payload: InsuranceRatingPlanSimulationPayload
  ): Observable<InsuranceRatingPlanSimulationResponse> {
    return this.http.post<InsuranceRatingPlanSimulationResponse>(`${this.base}/rating-plans/${ratingPlanId}/simulate`, payload);
  }

  getPolicy(id: string): Observable<InsurancePolicyListItem | null> {
    return this.http.get<InsurancePolicyListItem>(`${this.base}/policies/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => this.listPolicies().pipe(map(items => items.find(item => item.id === id) ?? null)))
    );
  }

  createPolicy(payload: CreateInsurancePolicyPayload): Observable<InsurancePolicyListItem> {
    return this.http.post<InsurancePolicyListItem>(`${this.base}/policies`, payload);
  }

  updatePolicy(id: string, payload: UpdateInsurancePolicyPayload): Observable<InsurancePolicyListItem> {
    return this.http.put<InsurancePolicyListItem>(`${this.base}/policies/${id}`, payload);
  }

  issuePolicy(id: string): Observable<InsurancePolicyListItem> {
    return this.http.post<InsurancePolicyListItem>(`${this.base}/policies/${id}/issue`, {});
  }

  suspendPolicy(id: string, payload: InsurancePolicyLifecycleActionPayload): Observable<InsurancePolicyListItem> {
    return this.http.post<InsurancePolicyListItem>(`${this.base}/policies/${id}/suspend`, payload);
  }

  reinstatePolicy(id: string, payload: InsurancePolicyLifecycleActionPayload): Observable<InsurancePolicyListItem> {
    return this.http.post<InsurancePolicyListItem>(`${this.base}/policies/${id}/reinstate`, payload);
  }

  cancelPolicy(id: string, payload: InsurancePolicyLifecycleActionPayload): Observable<InsurancePolicyListItem> {
    return this.http.post<InsurancePolicyListItem>(`${this.base}/policies/${id}/cancel`, payload);
  }

  expirePolicy(id: string): Observable<InsurancePolicyListItem> {
    return this.http.post<InsurancePolicyListItem>(`${this.base}/policies/${id}/expire`, {});
  }

  listPolicyTerms(id: string): Observable<InsurancePolicyTerm[]> {
    return this.http.get<InsurancePolicyTerm[]>(`${this.base}/policies/${id}/terms`).pipe(
      catchError(() => of([]))
    );
  }

  listPolicyCoverages(id: string): Observable<InsurancePolicyCoverage[]> {
    return this.http.get<InsurancePolicyCoverage[]>(`${this.base}/policies/${id}/coverages`).pipe(
      catchError(() => of([]))
    );
  }

  upsertPolicyCoverages(id: string, payload: UpsertInsurancePolicyCoveragesPayload): Observable<InsurancePolicyCoverage[]> {
    return this.http.put<InsurancePolicyCoverage[]>(`${this.base}/policies/${id}/coverages`, payload);
  }

  listPolicyPartyRoles(id: string): Observable<InsurancePolicyPartyRole[]> {
    return this.http.get<InsurancePolicyPartyRole[]>(`${this.base}/policies/${id}/party-roles`).pipe(
      catchError(() => of([]))
    );
  }

  upsertPolicyPartyRoles(id: string, payload: UpsertInsurancePolicyPartyRolesPayload): Observable<InsurancePolicyPartyRole[]> {
    return this.http.put<InsurancePolicyPartyRole[]>(`${this.base}/policies/${id}/party-roles`, payload);
  }

  listPolicyEndorsements(id: string): Observable<InsuranceEndorsement[]> {
    return this.http.get<InsuranceEndorsement[]>(`${this.base}/policies/${id}/endorsements`).pipe(
      catchError(() => of([]))
    );
  }

  createPolicyEndorsement(id: string, payload: CreateInsuranceEndorsementPayload): Observable<InsuranceEndorsement> {
    return this.http.post<InsuranceEndorsement>(`${this.base}/policies/${id}/endorsements`, payload);
  }

  applyPolicyEndorsement(id: string, endorsementId: string, reason: string | null): Observable<InsuranceEndorsement> {
    return this.http.post<InsuranceEndorsement>(`${this.base}/policies/${id}/endorsements/${endorsementId}/apply`, { reason });
  }

  listPolicyRenewalOffers(id: string): Observable<InsuranceRenewalOffer[]> {
    return this.http.get<InsuranceRenewalOffer[]>(`${this.base}/policies/${id}/renewal-offers`).pipe(
      catchError(() => of([]))
    );
  }

  createPolicyRenewalOffer(id: string, payload: CreateInsuranceRenewalOfferPayload): Observable<InsuranceRenewalOffer> {
    return this.http.post<InsuranceRenewalOffer>(`${this.base}/policies/${id}/renewal-offers`, payload);
  }

  renewPolicy(id: string, payload: RenewInsurancePolicyPayload): Observable<InsurancePolicyListItem> {
    return this.http.post<InsurancePolicyListItem>(`${this.base}/policies/${id}/renew`, payload);
  }

  listPolicyAuditLogs(id: string): Observable<InsuranceAuditLogItem[]> {
    return this.http.get<InsuranceAuditLogItem[]>(`${this.base}/policies/${id}/audit`).pipe(
      catchError(() => of([]))
    );
  }

  listPolicyDocuments(id: string): Observable<InsurancePolicyDocument[]> {
    return this.http.get<InsurancePolicyDocument[]>(`${this.base}/policies/${id}/documents`).pipe(
      catchError(() => of([]))
    );
  }

  listPolicyDocumentTemplates(): Observable<InsurancePolicyDocumentTemplate[]> {
    return this.http.get<InsurancePolicyDocumentTemplate[]>(`${this.base}/document-templates`).pipe(
      catchError(() => of([]))
    );
  }

  createPolicyDocumentTemplate(payload: UpsertInsurancePolicyDocumentTemplatePayload): Observable<InsurancePolicyDocumentTemplate> {
    return this.http.post<InsurancePolicyDocumentTemplate>(`${this.base}/document-templates`, payload);
  }

  updatePolicyDocumentTemplate(id: string, payload: UpsertInsurancePolicyDocumentTemplatePayload): Observable<InsurancePolicyDocumentTemplate> {
    return this.http.put<InsurancePolicyDocumentTemplate>(`${this.base}/document-templates/${id}`, payload);
  }

  activatePolicyDocumentTemplate(id: string): Observable<InsurancePolicyDocumentTemplate> {
    return this.http.post<InsurancePolicyDocumentTemplate>(`${this.base}/document-templates/${id}/activate`, {});
  }

  renderPolicyDraftPreview(policyId: string, payload: InsurancePolicyDraftRenderPayload): Observable<InsurancePolicyDraftRenderResult> {
    return this.http.post<InsurancePolicyDraftRenderResult>(`${this.base}/policies/${policyId}/draft-preview`, payload);
  }

  listPolicyOptions(): Observable<InsurancePolicyOption[]> {
    return this.http.get<InsurancePolicyOption[]>(`${this.base}/policies/options`).pipe(
      catchError(() => of([]))
    );
  }

  listClaims(): Observable<InsuranceClaimListItem[]> {
    return this.http.get<InsuranceClaimListItem[]>(`${this.base}/claims`).pipe(
      catchError(() => of(FALLBACK_CLAIMS))
    );
  }

  getClaim(id: string): Observable<InsuranceClaimDetail | null> {
    return this.http.get<InsuranceClaimDetail>(`${this.base}/claims/${id}`).pipe(
      map(item => item ?? null),
      catchError(() => of(null))
    );
  }

  createClaim(payload: CreateInsuranceClaimPayload): Observable<InsuranceClaimDetail> {
    return this.http.post<InsuranceClaimDetail>(`${this.base}/claims`, payload);
  }

  updateClaim(id: string, payload: UpdateInsuranceClaimPayload): Observable<InsuranceClaimDetail> {
    return this.http.put<InsuranceClaimDetail>(`${this.base}/claims/${id}`, payload);
  }

  addClaimReserve(id: string, payload: CreateInsuranceClaimReservePayload): Observable<InsuranceClaimReserve> {
    return this.http.post<InsuranceClaimReserve>(`${this.base}/claims/${id}/reserves`, payload);
  }

  approveClaim(id: string, payload: ApproveInsuranceClaimPayload): Observable<InsuranceClaimDetail> {
    return this.http.post<InsuranceClaimDetail>(`${this.base}/claims/${id}/approve`, payload);
  }

  declineClaim(id: string, payload: DeclineInsuranceClaimPayload): Observable<InsuranceClaimDetail> {
    return this.http.post<InsuranceClaimDetail>(`${this.base}/claims/${id}/decline`, payload);
  }

  createClaimPayment(id: string, payload: CreateInsuranceClaimPaymentPayload): Observable<InsuranceClaimPayment> {
    return this.http.post<InsuranceClaimPayment>(`${this.base}/claims/${id}/payments`, payload);
  }

  closeClaim(id: string, payload: CloseInsuranceClaimPayload): Observable<InsuranceClaimDetail> {
    return this.http.post<InsuranceClaimDetail>(`${this.base}/claims/${id}/close`, payload);
  }

  createClaimRecovery(id: string, payload: CreateInsuranceClaimRecoveryPayload): Observable<InsuranceClaimRecovery> {
    return this.http.post<InsuranceClaimRecovery>(`${this.base}/claims/${id}/recoveries`, payload);
  }

  addHealthClaimLine(id: string, payload: CreateInsuranceHealthClaimLinePayload): Observable<InsuranceHealthClaimLine> {
    return this.http.post<InsuranceHealthClaimLine>(`${this.base}/claims/${id}/health-lines`, payload);
  }

  getAiRecommendation(id: string): Observable<InsuranceAiRecommendationDetailRecord> {
    return this.http.get<InsuranceAiRecommendationDetailRecord>(`${this.base}/ai/recommendations/${id}`);
  }

  listAiRecommendations(params: {
    entityType?: string;
    entityId?: string;
    recommendationType?: string;
  }): Observable<InsuranceAiRecommendationRecord[]> {
    const query = new URLSearchParams();
    if (params.entityType) query.set('entityType', params.entityType);
    if (params.entityId) query.set('entityId', params.entityId);
    if (params.recommendationType) query.set('recommendationType', params.recommendationType);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.http.get<InsuranceAiRecommendationRecord[]>(`${this.base}/ai/recommendations${suffix}`).pipe(
      catchError(() => of([]))
    );
  }

  acceptAiRecommendation(id: string): Observable<InsuranceAiRecommendationRecord> {
    return this.http.post<InsuranceAiRecommendationRecord>(`${this.base}/ai/recommendations/${id}/accept`, null);
  }

  overrideAiRecommendation(id: string, payload: { reason: string }): Observable<unknown> {
    return this.http.post(`${this.base}/ai/recommendations/${id}/override`, payload);
  }

  copilotChat(payload: InsuranceAiCopilotChatPayload): Observable<InsuranceAiCopilotChatResult> {
    return this.http.post<InsuranceAiCopilotChatResult>(`${this.base}/ai/copilot/chat`, payload);
  }

  generateUwRiskScore(uwCaseId: string, force = false): Observable<InsuranceAiRecommendationRecord> {
    return this.http.post<InsuranceAiRecommendationRecord>(`${this.base}/ai/uw-cases/${uwCaseId}/risk-score`, { force });
  }

  listFraudAlerts(params: {
    claimId?: string;
    quoteId?: string;
    status?: string;
  }): Observable<InsuranceFraudAlertRecord[]> {
    const query = new URLSearchParams();
    if (params.claimId) query.set('claimId', params.claimId);
    if (params.quoteId) query.set('quoteId', params.quoteId);
    if (params.status) query.set('status', params.status);
    const suffix = query.toString() ? `?${query.toString()}` : '';
    return this.http.get<InsuranceFraudAlertRecord[]>(`${this.base}/ai/fraud-alerts${suffix}`).pipe(
      catchError(() => of([]))
    );
  }

  evaluateFraudAlert(payload: { claimId?: string; quoteId?: string }): Observable<InsuranceFraudAlertRecord[]> {
    return this.http.post<InsuranceFraudAlertRecord[]>(`${this.base}/ai/fraud-alerts/evaluate`, payload);
  }

  dismissFraudAlert(id: string): Observable<InsuranceFraudAlertRecord> {
    return this.http.post<InsuranceFraudAlertRecord>(`${this.base}/ai/fraud-alerts/${id}/dismiss`, null);
  }

  escalateFraudAlert(id: string): Observable<InsuranceFraudAlertRecord> {
    return this.http.post<InsuranceFraudAlertRecord>(`${this.base}/ai/fraud-alerts/${id}/escalate`, null);
  }
}

export interface InsuranceAiRecommendationDetailRecord {
  recommendation: InsuranceAiRecommendationRecord;
  overrides: InsuranceAiOverrideRecord[];
}

export interface InsuranceAiOverrideRecord {
  id: string;
  recommendationId: string;
  reason: string;
  userId: string;
  overriddenAtUtc: string;
}

export interface InsuranceAiRecommendationRecord {
  id: string;
  entityType: string;
  entityId: string;
  recommendationType: string;
  score: number;
  explanationJson: string;
  modelVersion: string;
  inputsHash: string;
  status: 'Pending' | 'Accepted' | 'Overridden' | 'Expired' | string;
  createdAtUtc: string;
}

export interface InsuranceFraudAlertRecord {
  id: string;
  claimId?: string | null;
  quoteId?: string | null;
  severity: string;
  rulesTriggered: string[];
  status: string;
  blocksAutoApprove: boolean;
  explanationJson: string;
  modelVersion: string;
  createdAtUtc: string;
}

export interface InsuranceAiCopilotChatPayload {
  entityType: string;
  entityId: string;
  message?: string;
  contextJson?: string;
}

export interface InsuranceKnowledgeCitation {
  sourceId: string;
  documentId: string;
  documentTitle: string;
  documentUri?: string | null;
  chunkIndex: number;
  documentVersion: number;
  score: number;
}

export interface InsuranceAiCopilotChatResult {
  recommendationId: string;
  answer: string;
  modelVersion: string;
  score: number;
  explanationJson: string;
  isGrounded: boolean;
  citations: InsuranceKnowledgeCitation[];
}
